import { ampli } from '@/ampli';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { RefObject, useCallback, useState } from 'react';
import { useDebounce } from 'react-use';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { WalletAvatar } from '@/ui/Avatars/WalletAvatar';
import { AlertCircleIcon } from '@/ui/Icons/AlertCircleIcon';

import { formatShortAddress } from '@/utils/address';
import { classNames } from '@/utils/classnames';
import { ChainAddress, isEthereumAddress } from '@/utils/models';

interface RecipientInputContentProps {
  chainId: number;
  currentAddress: ChainAddress | undefined;
  onConfirm: (address: ChainAddress) => void;
  onReset: () => void;
  onClose: () => void;
  inputRef: RefObject<HTMLInputElement>;
  showConnectWallet?: boolean;
  connectedWallet?: MatchaConnectedWallet<ConnectedWallet>;
  hasCustomRecipient?: boolean;
}

export const RecipientInput = ({
  chainId,
  currentAddress,
  onConfirm,
  onReset,
  onClose,
  inputRef,
  connectedWallet,
  hasCustomRecipient = false,
}: RecipientInputContentProps) => {
  const [address, setAddress] = useState<string | undefined>(currentAddress);
  const [addressError, setAddressError] = useState<string | undefined>();

  // Debounced validation only drives the inline error; Confirm validates the
  // current input synchronously below so it never races this 250ms timer.
  useDebounce(
    () => {
      if (!address || !chainId) {
        setAddressError(undefined);
        return;
      }
      setAddressError(isEthereumAddress(address) ? undefined : 'Invalid Ethereum wallet address');
    },
    250,
    [address, chainId],
  );

  const resetAddresses = useCallback(() => {
    setAddress(undefined);
    setAddressError(undefined);
    onReset();
  }, [onReset]);

  const onConfirmClick = () => {
    if (!address || !isEthereumAddress(address)) {
      setAddressError('Invalid Ethereum wallet address');
      return;
    }
    ampli.recipientAddressUpdated({ recipient_address: address });
    onConfirm(address as ChainAddress);
    onClose();
  };

  const onSwitchToConnectedWallet = () => {
    ampli.recipientAddressUpdated({ recipient_address: 'connected_wallet' });
    resetAddresses();
    onClose();
  };

  return (
    <div className="p-2 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <div className="flex items-center flex-grow">
            <input
              ref={inputRef}
              className={fontStyles({
                variant: 'LABEL_LG',
                class: [
                  'bg-token-alphalight-200',
                  'relative',
                  'w-full',
                  'h-9',
                  'rounded-sm',
                  'border-0',
                  'active:border-token-purple-100',
                  'px-3',
                  'transition-all',
                  'duration-200',
                  'ease-in-out',
                  '[&::-webkit-search-cancel-button]:hidden',
                  'placeholder:text-token-gray-700',
                  'focus:outline-token-purple-900',
                  'focus:outline-2',
                  'focus-visible:outline-token-purple-900',
                  'focus-visible:outline-2',
                  'hover:outline-token-alphalight-500',
                  'hover:outline-1',
                  'data-[is-invalid="true"]:outline-token-red-900',
                  'data-[is-invalid="true"]:outline-2',
                  'data-[is-invalid="true"]:pr-8',
                  'data-[is-invalid="true"]:-mr-6',
                ],
              })}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={'Paste Ethereum wallet address...'}
              type="text"
              spellCheck="false"
              autoComplete="off"
              autoCorrect="off"
              data-is-invalid={!!addressError}
            />
            {addressError && (
              <span className="text-token-red-900 w-4 h-4">
                <AlertCircleIcon />
              </span>
            )}
          </div>
          {!addressError && address && (
            <button
              className={buttons({
                color: 'purple',
                size: 'small',
                shape: 'rounded',
              })}
              onClick={onConfirmClick}
              disabled={!!addressError}
            >
              Confirm
            </button>
          )}
        </div>
        {addressError && (
          <div
            className={fontStyles({ variant: 'LABEL_MD', class: ['px-1', 'text-token-red-900'] })}
          >
            {addressError}
          </div>
        )}
      </div>
      {connectedWallet && hasCustomRecipient && (
        <>
          <div className="w-full h-px bg-[linear-gradient(to_right,transparent_0%,var(--color-token-gray-300)_10%,var(--color-token-gray-300)_90%,transparent_100%)]" />

          <button
            className={classNames(
              fontStyles({ variant: 'LABEL_MD' }),
              buttons({
                color: 'white',
                size: 'medium',
                shape: 'pill',
                class: 'flex items-center justify-center',
              }),
            )}
            onClick={onSwitchToConnectedWallet}
          >
            <span>Switch to</span>
            <WalletAvatar wallet={connectedWallet} size={20} className="mx-1" />
            <span>{formatShortAddress(connectedWallet.address)}</span>
            <span className="text-token-gray-700">(connected)</span>
          </button>
        </>
      )}
    </div>
  );
};
